import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { RentalUnitUser } from '../../types';

interface RentalUnitUsersState {
  rentalUnitUsers: RentalUnitUser[];
  selectedRentalUnitUser: RentalUnitUser | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: RentalUnitUsersState = {
  rentalUnitUsers: [],
  selectedRentalUnitUser: null,
  isLoading: false,
  error: null
};

// Fetch rental unit users (tenants) for a specific unit
export const fetchRentalUnitUsers = createAsyncThunk(
  'rentalUnitUsers/fetchRentalUnitUsers',
  async (rentalUnitId: string) => {
    const response = await api.get(`/rental-units/${rentalUnitId}/tenants`);
    return response.data.data;
  }
);

// Add tenant to rental unit
export const addTenantToUnit = createAsyncThunk(
  'rentalUnitUsers/addTenantToUnit',
  async ({ unitId, tenantData }: { unitId: string; tenantData: any }) => {
    const response = await api.post(`/rental-units/${unitId}/tenants`, tenantData);
    return response.data.data;
  }
);

// Update tenant in rental unit
export const updateTenantInUnit = createAsyncThunk(
  'rentalUnitUsers/updateTenantInUnit',
  async ({ unitId, tenantId, updateData }: { unitId: string; tenantId: string; updateData: any }) => {
    const response = await api.put(`/rental-units/${unitId}/tenants/${tenantId}`, updateData);
    return response.data.data;
  }
);

// Remove tenant from rental unit
export const removeTenantFromUnit = createAsyncThunk(
  'rentalUnitUsers/removeTenantFromUnit',
  async ({ unitId, tenantId }: { unitId: string; tenantId: string }) => {
    await api.delete(`/rental-units/${unitId}/tenants/${tenantId}`);
    return tenantId;
  }
);

const rentalUnitUsersSlice = createSlice({
  name: 'rentalUnitUsers',
  initialState,
  reducers: {
    clearRentalUnitUsers: (state) => {
      state.rentalUnitUsers = [];
      state.selectedRentalUnitUser = null;
      state.error = null;
    },
    setSelectedRentalUnitUser: (state, action: PayloadAction<RentalUnitUser | null>) => {
      state.selectedRentalUnitUser = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch rental unit users
      .addCase(fetchRentalUnitUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRentalUnitUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rentalUnitUsers = action.payload;
      })
      .addCase(fetchRentalUnitUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch rental unit users';
      })
      // Add tenant to unit
      .addCase(addTenantToUnit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addTenantToUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rentalUnitUsers.push(action.payload);
      })
      .addCase(addTenantToUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to add tenant to unit';
      })
      // Update tenant in unit
      .addCase(updateTenantInUnit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTenantInUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.rentalUnitUsers.findIndex(user => user._id === action.payload._id);
        if (index !== -1) {
          state.rentalUnitUsers[index] = action.payload;
        }
        if (state.selectedRentalUnitUser?._id === action.payload._id) {
          state.selectedRentalUnitUser = action.payload;
        }
      })
      .addCase(updateTenantInUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update tenant in unit';
      })
      // Remove tenant from unit
      .addCase(removeTenantFromUnit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeTenantFromUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rentalUnitUsers = state.rentalUnitUsers.filter(user => {
          const userId = typeof user.userId === 'string' ? user.userId : user.userId._id;
          return userId !== action.payload;
        });
        if (state.selectedRentalUnitUser) {
          const selectedUserId = typeof state.selectedRentalUnitUser.userId === 'string' 
            ? state.selectedRentalUnitUser.userId 
            : state.selectedRentalUnitUser.userId._id;
          if (selectedUserId === action.payload) {
            state.selectedRentalUnitUser = null;
          }
        }
      })
      .addCase(removeTenantFromUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to remove tenant from unit';
      });
  }
});

export const { clearRentalUnitUsers, setSelectedRentalUnitUser } = rentalUnitUsersSlice.actions;
export default rentalUnitUsersSlice.reducer;